import React, { useState, useEffect, useContext } from 'react';
import { planAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { AdminContext } from '../components/layout/Layout';
import { Rocket, ShieldCheck, Zap, Diamond, CheckCircle, Clock, ArrowRight, Plus, Pencil, Trash2, Briefcase, TrendingUp, Gem, Loader } from 'lucide-react';
import InvestmentModal from '../components/dashboard/InvestmentModal';
import { planAPI as planCreateAPI } from '../services/api';

const ICONS = [Briefcase, TrendingUp, Gem, Diamond];
const ICON_COLORS = ['icon-blue', 'icon-purple', 'icon-orange', 'icon-green'];

const SEED_PORTFOLIOS = [
  { name: 'Starter Growth', minimum: 1000, maximum: 9999, dailyProfitMin: '0.3', dailyProfitMax: '0.6', referralBonus: 3, endDate: '14', description: 'An accessible entry point into institutional-grade strategies. Ideal for new investors looking to experience algorithmic yield generation with a modest capital commitment.' },
  { name: 'Balanced Yield', minimum: 5000, maximum: 49999, dailyProfitMin: '0.4', dailyProfitMax: '0.8', referralBonus: 4, endDate: '21', description: 'A diversified, medium-term strategy blending blue-chip digital assets with short-duration credit instruments for balanced risk-adjusted returns.' },
  { name: 'Core Income Portfolio', minimum: 10000, maximum: 100000, dailyProfitMin: '0.5', dailyProfitMax: '1.0', referralBonus: 5, endDate: '30', description: 'A low-volatility strategy focused on steady yield generation through short-term credit instruments and blue-chip dividend arbitrage. Ideal for capital preservation with consistent returns.' },
  { name: 'Alpha Multi-Strategy', minimum: 50000, maximum: 500000, dailyProfitMin: '1.2', dailyProfitMax: '2.0', referralBonus: 8, endDate: '90', description: 'Our flagship quantitative strategy utilizing machine learning to capture cross-asset arbitrage opportunities across Forex, Digital Assets, and Commodities markets.' },
  { name: 'Special Situations Fund', minimum: 250000, maximum: 5000000, dailyProfitMin: '2.5', dailyProfitMax: '3.5', referralBonus: 12, endDate: '180', description: 'High-conviction, illiquid opportunities in distressed assets, late-stage venture capital, and tokenized real-world assets. Designed exclusively for UHNW and Family Offices.' },
];

function Plans() {
  const { user } = useAuth();
  const { isAdmin } = useContext(AdminContext);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editPlanId, setEditPlanId] = useState(null);

  const [formData, setFormData] = useState({
    name: '', minimum: '', maximum: '', dailyProfitMin: '', dailyProfitMax: '', referralBonus: '', endDate: '', description: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchPlans();
  }, [isAdmin]);

  const fetchPlans = async () => {
    try {
      const response = await planAPI.viewAllPlans();
      const fetchedPlans = response.data?.message || [];
      const sortedPlans = [...fetchedPlans].sort((a, b) => {
        const cleanNumber = (str) => parseFloat(String(str).replace(/[^0-9.-]/g, '')) || 0;
        const minA = cleanNumber(a.minimum);
        const minB = cleanNumber(b.minimum);
        if (minA === minB) return (parseInt(a.endDate) || 0) - (parseInt(b.endDate) || 0);
        return minA - minB;
      });
      setPlans(sortedPlans);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error fetching plans' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });
    try {
      if (editPlanId) {
        await planAPI.editPlan(editPlanId, formData);
        setMessage({ type: 'success', text: 'Investment plan updated successfully!' });
      } else {
        await planAPI.createPlan(formData);
        setMessage({ type: 'success', text: 'Investment plan created successfully!' });
      }
      setFormData({ name: '', minimum: '', maximum: '', dailyProfitMin: '', dailyProfitMax: '', referralBonus: '', endDate: '', description: '' });
      setEditPlanId(null);
      setShowForm(false);
      fetchPlans();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error saving plan' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (plan) => {
    setFormData({
      name: plan.name || '',
      minimum: plan.minimum || '',
      maximum: plan.maximum || '',
      dailyProfitMin: plan.dailyProfitMin || '',
      dailyProfitMax: plan.dailyProfitMax || '',
      referralBonus: plan.referralBonus || '',
      endDate: plan.endDate || '',
      description: plan.description || ''
    });
    setEditPlanId(plan._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      await planAPI.deletePlan(id);
      fetchPlans();
    } catch (error) {
       console.error(error);
       alert("Error deleting plan");
    }
  };

  const handleInvestClick = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const seedPortfolios = async () => {
    setSeeding(true);
    setMessage({ type: '', text: '' });
    try {
      const existingNames = plans.map(p => p.name.toLowerCase());
      const missing = SEED_PORTFOLIOS.filter(p => !existingNames.includes(p.name.toLowerCase()));
      if (missing.length === 0) {
        setMessage({ type: 'success', text: 'All institutional portfolios are already configured.' });
        setSeeding(false);
        return;
      }
      for (const portfolio of missing) {
        await planAPI.createPlan(portfolio);
      }
      setMessage({ type: 'success', text: `${missing.length} new portfolio${missing.length > 1 ? 's' : ''} configured successfully.` });
      fetchPlans();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error seeding portfolios.' });
    } finally {
      setSeeding(false);
    }
  };

  const missingPortfolios = SEED_PORTFOLIOS.filter(sp => !plans.some(p => p.name.toLowerCase() === sp.name.toLowerCase()));

  if (loading) {
    return (
      <div className="dashboard-page" style={{display:'flex', justifyContent:'center', alignItems:'center', minHeight:'50vh'}}>
        <div className="spinner" style={{borderColor: 'var(--border-color)', borderTopColor: 'var(--accent-primary)'}}></div>
      </div>
    );
  }

  return (
    <div className="dashboard-page overflow-hidden">
      <div className="dashboard-header" style={{ marginBottom: '2rem' }}>
        <div>
          <h1>Model Portfolios & Allocations</h1>
          <p>Institutional-grade strategies tailored to your investment objectives</p>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {missingPortfolios.length > 0 && (
              <button
                className="btn btn-primary glass"
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.2)' }}
                onClick={seedPortfolios}
                disabled={seeding}
              >
                {seeding ? <><Loader size={16} className="spinning" /> Adding...</> : <><Plus size={16} /> Add {missingPortfolios.length} Missing Portfolio{missingPortfolios.length > 1 ? 's' : ''}</>}
              </button>
            )}
            <button 
              className="btn btn-primary glass" 
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}
              onClick={() => {
                if (showForm) {
                  setShowForm(false);
                  setEditPlanId(null);
                  setFormData({ name: '', minimum: '', maximum: '', dailyProfitMin: '', dailyProfitMax: '', referralBonus: '', endDate: '', description: '' });
                } else {
                  setShowForm(true);
                }
              }}
            >
              <Plus size={20} /> {showForm ? 'Cancel' : 'Add Plan'}
            </button>
          </div>
        )}
      </div>

      {message.text && (
        <div className={message.type === 'error' ? 'error-message' : 'success-message'} style={{marginBottom: '2rem', borderRadius: '12px'}}>
          {message.text}
        </div>
      )}

      {isAdmin && showForm && (
        <div className="dashboard-panel glass" style={{ marginBottom: '2.5rem', border: '1px solid var(--accent-primary)', padding: '2rem' }}>
          <div className="panel-header" style={{ marginBottom: '1.5rem', border: 'none' }}>
            <h2 className="panel-title" style={{ fontSize: '1.4rem' }}>{editPlanId ? 'Edit Investment Plan' : 'Create New Investment Plan'}</h2>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
              <div className="form-group">
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>Plan Name</label>
                <input type="text" name="name" className="glass" placeholder="e.g. Diamond Plan" value={formData.name} onChange={handleChange} required style={{ width: '100%', padding: '0.85rem' }} />
              </div>
              <div className="form-group">
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>Duration (Days)</label>
                <input type="number" name="endDate" className="glass" placeholder="e.g. 30" value={formData.endDate} onChange={handleChange} required style={{ width: '100%', padding: '0.85rem' }} />
              </div>
              <div className="form-group">
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>Min Amount ($)</label>
                <input type="number" name="minimum" className="glass" placeholder="100" value={formData.minimum} onChange={handleChange} required style={{ width: '100%', padding: '0.85rem' }} />
              </div>
              <div className="form-group">
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>Max Amount ($)</label>
                <input type="number" name="maximum" className="glass" placeholder="5000" value={formData.maximum} onChange={handleChange} required style={{ width: '100%', padding: '0.85rem' }} />
              </div>
              <div className="form-group">
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>Daily Profit Min (%)</label>
                <input type="text" name="dailyProfitMin" className="glass" placeholder="1.5" value={formData.dailyProfitMin} onChange={handleChange} required style={{ width: '100%', padding: '0.85rem' }} />
              </div>
              <div className="form-group">
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>Daily Profit Max (%)</label>
                <input type="text" name="dailyProfitMax" className="glass" placeholder="3.0" value={formData.dailyProfitMax} onChange={handleChange} required style={{ width: '100%', padding: '0.85rem' }} />
              </div>
              <div className="form-group">
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>Referral Bonus (%)</label>
                <input type="number" name="referralBonus" className="glass" placeholder="10" value={formData.referralBonus} onChange={handleChange} required style={{ width: '100%', padding: '0.85rem' }} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>Description (Optional)</label>
                <textarea name="description" className="glass" placeholder="Plan details..." value={formData.description} onChange={handleChange} style={{ width: '100%', padding: '0.85rem', minHeight: '80px', resize: 'vertical' }} />
              </div>
            </div>

            {/* Live Preview Bar */}
            {(formData.name || formData.minimum) && (
              <div className="glass" style={{
                background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.18)',
                borderRadius: '12px', padding: '1rem 1.5rem', marginBottom: '2rem',
                display: 'flex', flexWrap: 'wrap', gap: '1rem 2rem', alignItems: 'center'
              }}>
                <span style={{ fontSize: '0.75rem', fontWeight: '800', color: 'var(--accent-primary)', textTransform: 'uppercase', letterSpacing: '1px' }}>Preview</span>
                {formData.name && <span style={{ fontWeight: '600' }}>{formData.name}</span>}
                {formData.minimum && <span className="text-success">${Number(formData.minimum).toLocaleString()} Min</span>}
                {formData.dailyProfitMin && <span className="text-warning">{formData.dailyProfitMin}% Return</span>}
                {formData.endDate && <span className="text-accent">{formData.endDate} Days</span>}
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button 
                type="submit" 
                disabled={submitting} 
                className="btn btn-primary" 
                style={{ flex: 1, padding: '1rem', background: 'var(--accent-gradient)', border: 'none', borderRadius: '12px', fontWeight: '700' }}
              >
                {submitting ? 'Saving...' : editPlanId ? 'Update Plan' : 'Create Plan'}
              </button>
              <button 
                type="button" 
                onClick={() => { setShowForm(false); setEditPlanId(null); setFormData({ name: '', minimum: '', maximum: '', dailyProfitMin: '', dailyProfitMax: '', referralBonus: '', endDate: '', description: '' }); }} 
                className="btn glass" 
                style={{ padding: '1rem 2rem', borderRadius: '12px' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {plans.length === 0 ? (
        <div className="dashboard-panel glass text-center" style={{ padding: '4rem 2rem' }}>
           <div className="stat-icon-wrapper icon-blue" style={{ margin: '0 auto 1.5rem' }}>
              <Briefcase size={32} />
           </div>
           <h2 className="panel-title">No Portfolios Configured</h2>
           <p className="text-secondary" style={{ maxWidth: '500px', margin: '0.5rem auto 2rem' }}>Model portfolio strategies will appear here once configured by the investment committee.</p>
           {isAdmin && (
             <button
               className="btn btn-primary"
               style={{ padding: '1rem 2.5rem', borderRadius: '12px', fontWeight: '700', background: 'var(--accent-gradient)', border: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.6rem' }}
               onClick={seedPortfolios}
               disabled={seeding}
             >
               {seeding ? <><Loader size={18} className="spinning" /> Configuring...</> : <><Plus size={18} /> Seed Institutional Portfolios</>}
             </button>
           )}
        </div>
      ) : (
        <div className="plans-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem' }}>
          {plans.map((plan, index) => {
            const IconComp = ICONS[index % ICONS.length];
            const iconCol = ICON_COLORS[index % ICON_COLORS.length];
            
            return (
              <div className="stat-card-new glass" key={plan._id || index} style={{ padding: '2rem', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {isAdmin && (
                  <div style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', display: 'flex', gap: '0.6rem' }}>
                    <button 
                      onClick={() => handleEditClick(plan)}
                      title="Edit Plan"
                      style={{ 
                        background: 'rgba(255,255,255,0.05)', 
                        border: '1px solid rgba(255,255,255,0.1)', 
                        color: 'var(--text-secondary)', 
                        padding: '0.5rem', 
                        borderRadius: '8px', 
                        cursor: 'pointer', 
                        display: 'flex',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-primary)'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.3)'; e.currentTarget.style.background = 'rgba(99,102,241,0.06)'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                    >
                      <Pencil size={15} />
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(plan._id)}
                      title="Delete Plan"
                      style={{ 
                        background: 'rgba(255,255,255,0.05)', 
                        border: '1px solid rgba(255,255,255,0.1)', 
                        color: 'var(--text-secondary)', 
                        padding: '0.5rem', 
                        borderRadius: '8px', 
                        cursor: 'pointer', 
                        display: 'flex',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)'; e.currentTarget.style.background = 'rgba(239,68,68,0.06)'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
                
                <div className="plan-header" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                   <div className={`stat-icon-wrapper ${iconCol}`}>
                      <IconComp size={24} />
                   </div>
                   <h3 className="plan-title" style={{ margin: 0, fontSize: '1.3rem', fontWeight: '700', color: 'white' }}>{plan.name}</h3>
                </div>
                
                <div style={{ marginBottom: '2rem', background: 'rgba(255,255,255,0.02)', padding: '1.5rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                   <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Minimum Allocation</div>
                   <div style={{ fontSize: '2.4rem', fontWeight: '800', color: 'var(--accent-primary)', display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '1.2rem', marginTop: '0.5rem', marginRight: '2px' }}>$</span>
                      {(parseFloat(plan.minimum) || 0).toLocaleString()}
                   </div>
                </div>
                
                <ul className="plan-features" style={{ listStyle: 'none', padding: 0, margin: '0 0 2rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                   <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.92rem' }}><CheckCircle className="text-success" size={18} /> <span>Maximum Allocation: <strong>${(parseFloat(plan.maximum) || 0).toLocaleString()}</strong></span></li>
                   <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.92rem' }}><CheckCircle className="text-success" size={18} /> <span>Target Daily Yield: <strong>{plan.dailyProfitMin}% – {plan.dailyProfitMax}%</strong></span></li>
                   <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.92rem' }}><CheckCircle className="text-success" size={18} /> <span>Referral Incentive: <strong>{plan.referralBonus}%</strong></span></li>
                   <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.92rem' }}><Clock className="text-warning" size={18} /> <span>Lock-up Period: <strong>{plan.endDate} Days</strong></span></li>
                </ul>
                
                {plan.description && (
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: '1.6', marginBottom: '2rem', flex: 1 }}>
                    {plan.description}
                  </p>
                )}
                
                <div style={{ marginTop: 'auto' }}>
                  <button className="btn btn-primary" style={{ width: '100%', padding: '1rem', borderRadius: '12px', fontWeight: '700', background: 'var(--accent-gradient)', border: 'none' }} onClick={() => handleInvestClick(plan)}>
                    Request Allocation <ArrowRight size={18} style={{ marginLeft: '0.5rem' }} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {isModalOpen && <InvestmentModal selectedPlan={selectedPlan} onClose={() => setIsModalOpen(false)} onSuccess={fetchPlans} />}
    </div>
  );
}

export default Plans;
